/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import { orderBy, cloneDeep } from 'lodash';
import Types from '../constants/actionTypes';
import { WorkPackageStatuses } from '../constants/storyStatusTypes';
import { extractChildren } from '../utils/hierarchyHelpers';
import { checkIfScrum } from '../utils/scrumHelper';

const initialState = {
  initialDataLoading: true,
  isFetching: false,
  isSending: false,

  workPackage: {},

  combinedReady: [],
  gettingReady: [],
  ready: [],
  sprint: [],
  readyNotSprint: [],
  accepted: [],
  completed: [],
  completedNotShippable: [],
  acceptedRegardlessCadence: [],
  shippable: [],
  shippableRegardlessCadence: [],

  all: [],
  activities: [],
  features: [],
  featuresAnalyzing: [],
  capabilities: [],
  capabilitiesAnalyzing: [],
  epics: [],
  epicsAnalyzing: [],
  stories: [],
  parents: [],
  metrics: {
    childrenProgress: {}
  },
  spaceSelected: false,
  selectedSpace: {
    type: '',
    id: ''
  }
};

const updateMultipeWorkPackages = (arrayOfWorkPackages, state) => {
  const updatedIds = arrayOfWorkPackages.map((wp) => wp.id);
  const shallowCopyOfStateResults = [...state.all];
  const updated = shallowCopyOfStateResults.map((wp) => {
    if (updatedIds.includes(wp.id)) {
      const updatedWorkPackage = arrayOfWorkPackages.find(
        (result) => result.id === wp.id
      );

      if (!updatedWorkPackage.result_type.type_attributes) {
        delete updatedWorkPackage.result_type;
      }
      return {
        ...wp,
        ...updatedWorkPackage
      };
    }
    return wp;
  });
  return updated;
};

const updateRanks = (workPackages, ranksAndIds) =>
  workPackages.map((wp) => {
    const rankObject = ranksAndIds.find((rankObj) => rankObj.id === wp.id);
    if (rankObject) return { ...wp, rank: rankObject.rank };
    return wp;
  });

const isSpaceSelected = (space) =>
  space && Object.keys(space).length > 0 && space.id !== 'unassigned';

const groupWorkPackages = (
  state,
  workPackages,
  cadenceId,
  isSending = false
) => {
  const { selectedSpace } = state;

  const scrumMode = checkIfScrum(selectedSpace);

  const grouped = {};
  const storedCadenceID = cadenceId;
  grouped.all = orderBy(workPackages, ['rank', 'id'], ['asc', 'desc']);

  grouped.activities = orderBy(
    workPackages.filter((item) => item.type === 'Activity'),
    'rank'
  );
  grouped.epics = orderBy(
    workPackages.filter((item) => item.type === 'Result' && item.level === 4),
    'rank'
  );

  grouped.epicsAnalyzing = grouped.epics.filter(
    (item) => item.state === WorkPackageStatuses.GETTING_READY
  );
  grouped.capabilities = orderBy(
    workPackages.filter((item) => item.type === 'Result' && item.level === 3),
    'rank'
  );

  grouped.capabilitiesAnalyzing = grouped.capabilities.filter(
    (item) => item.state === WorkPackageStatuses.GETTING_READY
  );

  grouped.features = orderBy(
    workPackages.filter((item) => item.type === 'Result' && item.level === 2),
    'rank'
  );

  grouped.featuresAnalyzing = grouped.features.filter(
    (item) => item.state === WorkPackageStatuses.GETTING_READY
  );

  const stories = orderBy(
    workPackages.filter(
      (item) =>
        item.type === 'Result' && (item.level === 1 || item.level === null)
    ),
    'rank'
  );

  grouped.stories = stories;
  grouped.combinedReady = stories.filter(
    (item) =>
      item.state === WorkPackageStatuses.GETTING_READY ||
      item.state === WorkPackageStatuses.READY
  );
  grouped.gettingReady = stories.filter(
    (item) => item.state === WorkPackageStatuses.GETTING_READY
  );
  grouped.ready = stories.filter(
    (item) => item.state === WorkPackageStatuses.READY
  );
  grouped.readyNotSprint = stories.filter(
    (item) =>
      item.state === WorkPackageStatuses.READY &&
      (!scrumMode || (!item.current_cadence_id && !item.current_cadence))
  );
  grouped.sprint = stories.filter((item) =>
    scrumMode
      ? !!item.cadence_ids.find((id) => storedCadenceID === Number(id)) &&
        !item.completed &&
        item.state !== WorkPackageStatuses.LIVE
      : !item.completed &&
        item.state === WorkPackageStatuses.IN_PROGRESS &&
        item.state !== WorkPackageStatuses.LIVE
  );
  grouped.completed = stories.filter((item) =>
    scrumMode
      ? !!item.cadence_ids.find((id) => storedCadenceID === Number(id)) &&
        item.completed
      : item.completed
  );
  grouped.completedNotShippable = stories.filter(
    (item) => (item.accepted || item.completed) && !item.shippable
  );
  grouped.accepted = stories.filter((item) => item.accepted);
  grouped.acceptedRegardlessCadence = stories.filter((item) => item.accepted);

  grouped.shippable = stories.filter((item) => item.shippable);
  grouped.shippableRegardlessCadence = stories.filter((item) => item.shippable);

  return {
    ...state,
    isSending,
    initialDataLoading: false,
    isFetching: false,
    combinedReady: grouped.combinedReady,
    gettingReady: grouped.gettingReady,
    ready: grouped.ready,
    readyNotSprint: grouped.readyNotSprint,
    sprint: grouped.sprint,
    completed: grouped.completed,
    completedNotShippable: grouped.completedNotShippable,
    accepted: grouped.accepted,
    acceptedRegardlessCadence: grouped.acceptedRegardlessCadence,
    shippable: grouped.shippable,
    shippableRegardlessCadence: grouped.shippableRegardlessCadence,

    activities: grouped.activities,
    features: grouped.features,
    featuresAnalyzing: grouped.featuresAnalyzing,
    capabilities: grouped.capabilities,
    capabilitiesAnalyzing: grouped.capabilitiesAnalyzing,
    epics: grouped.epics,
    epicsAnalyzing: grouped.epicsAnalyzing,
    stories: grouped.stories,
    all: grouped.all
  };
};

const removeCadenceFromWorkPackages = (cadence, state, cadenceId) => {
  const updatedWorkPackages = state.all.map((workPackage) => {
    if (workPackage.cadence_ids.find((c) => c === cadence.id))
      return {
        ...workPackage,
        cadence_ids: workPackage.cadence_ids.filter((c) => c !== cadence.id),
        cadences: workPackage.cadences.filter((c) => c !== cadence.id),
        current_cadence_id:
          workPackage.current_cadence_id === cadence.id
            ? null
            : workPackage.current_cadence_id,
        current_cadence:
          workPackage.current_cadence?.id === cadence.id
            ? undefined
            : workPackage.current_cadence
      };
    return workPackage;
  });
  return groupWorkPackages(state, updatedWorkPackages, cadenceId); // cadenceId is the current active cadence, not the deleted cadence
};

const workPackageReducer = (state = initialState, action) => {
  let workPackages;
  let newWorkPackage;
  let ranks = {};
  let isSending = false;
  const { metrics } = state;
  const { cadenceId, product, team, teamId, productId, level } = action;
  let updatedSpace;
  let updatedState;
  let filteredWorkPackages;
  let parentResult;
  let metricsClone;
  switch (action.type) {
    case Types.REQUEST_WORK_PACKAGE:
      return { ...state, isFetching: true };
    case Types.REQUEST_WORK_PACKAGE_GRAPHS:
      return {
        ...state,
        metrics: {
          ...metrics,
          [action.workPackageId]: {
            ...metrics[action.workPackageId],
            [action.graph]: undefined
          }
        }
      };
    case Types.CANCEL_REQUEST_WORK_PACKAGE:
      return { ...state, isFetching: false };
    case Types.REQUEST_WORK_PACKAGE_FAILED:
      return { ...state, isSending: false, isFetching: false };
    case Types.RECEIVE_TARGET_WORK_PACKAGES:
    case Types.RECEIVE_ALL_WORK_PACKAGES:
    case Types.UPDATE_WORK_PACKAGE_LIST:
      // eslint-disable-next-line no-case-declarations
      const packagesToGroup = localStorage.selectedSpace
        ? action.workPackages || state.all
        : state.all;
      return groupWorkPackages(state, packagesToGroup, cadenceId);

    case Types.UPDATE_WORK_PACKAGE_CHILDREN:
      workPackages = state.all.map((workPackage) => {
        const updatedWorkPackage = action.workPackages.find(
          (wp) => wp.id === workPackage.id
        );
        if (updatedWorkPackage) {
          return updatedWorkPackage;
        }

        return workPackage;
      });

      return groupWorkPackages(state, workPackages, cadenceId);
    case Types.REMOVE_CHILDREN_FROM_WORK_PACKAGE:
      workPackages = state.all.map((workPackage) => {
        const wp = workPackage;
        if (wp.parent_id === action.workPackage.id) {
          wp.parent_id = null;
        }
        return wp;
      });

      return groupWorkPackages(state, workPackages, cadenceId);
    case Types.REPLACE_WORK_PACKAGE_CHILDREN:
      workPackages = state.all.map((workPackage) => {
        const wp = workPackage;
        if (wp.parent_id === action.oldParent.id) {
          wp.parent_id = action.oldParent.parent_id || null;
        }
        return wp;
      });

      return groupWorkPackages(state, workPackages, cadenceId);
    case Types.DELETE_WORK_PACKAGE_CHILDREN:
      workPackages = state.all.map((workPackage) => {
        const descendant = action.descendants.find(
          (d) => d.id === workPackage.id
        );
        if (descendant) {
          descendant.deleted_at = true;
          descendant.removed_parent_id = descendant.parent_id;
          return descendant;
        }
        return workPackage;
      });

      return groupWorkPackages(state, workPackages, cadenceId);
    case Types.ADD_WORK_PACKAGE:
      workPackages = state.all;
      ({ isSending, workPackage: newWorkPackage } = action);

      if (workPackages.findIndex((wp) => wp.id === newWorkPackage.id) === -1) {
        workPackages = state.all.concat(newWorkPackage);
      }
      parentResult = workPackages.find(
        (wp) => wp.id === newWorkPackage.parent_id
      );

      if (parentResult && !parentResult.has_children) {
        parentResult = {
          ...parentResult,
          has_children: true
        };
        workPackages = workPackages.map((wp) => {
          if (wp.id === parentResult.id) {
            return parentResult;
          }
          return wp;
        });
      }
      if (action.ranksAndIds) {
        workPackages = updateRanks(workPackages, action.ranksAndIds);
      }

      return groupWorkPackages(state, workPackages, cadenceId, isSending);
    case Types.UPDATE_WORK_PACKAGE_RANKS:
      workPackages = updateRanks(state.all, action.ranksAndIds);

      return groupWorkPackages(state, workPackages, cadenceId, isSending);
    case Types.UPDATE_WORK_PACKAGE:
      ranks = action.workPackage.ranks || {};
      workPackages = state.all.map((workPackage) => {
        const updatedWorkPackage = workPackage;
        updatedWorkPackage.rank = ranks[workPackage.id] || workPackage.rank;
        if (workPackage.id === action.workPackage.id) {
          return action.workPackage;
        }

        return updatedWorkPackage;
      });

      return groupWorkPackages(state, workPackages, action.cadenceId);
    case Types.UPDATE_MULTIPLE_WORK_PACKAGES:
      updatedState = updateMultipeWorkPackages(action.results, state);
      return groupWorkPackages(state, updatedState, cadenceId);

    case Types.UPDATE_WORK_PACKAGE_RANK:
      workPackages = state.all.map((workPackage) => {
        if (workPackage.id === action.workPackage.id)
          return { ...workPackage, rank: action.workPackage.rank };

        return workPackage;
      });

      return groupWorkPackages(state, workPackages, cadenceId);
    case Types.REMOVE_WORK_PACKAGES:
      filteredWorkPackages = state.all.filter(
        (workPackage) =>
          !action.workPackages.some((wp) => wp.id === workPackage.id)
      );

      return groupWorkPackages(state, filteredWorkPackages, cadenceId);
    case Types.DELETE_WORK_PACKAGE: {
      const children = extractChildren(
        action.workPackage.id,
        state.all,
        action.workPackage.level
      );
      filteredWorkPackages = state.all.filter(
        (workPackage) => workPackage.id !== action.workPackage.id
      );
      workPackages =
        action.workPackage.level !== 3
          ? filteredWorkPackages
          : filteredWorkPackages.map((workPackage) => {
              let updatedWorkPackage = workPackage;
              // if the updatedWorkPackage level is 2 and is a child of the dispatched result
              if (
                updatedWorkPackage.level === 2 &&
                children.findIndex(
                  (child) => child.id === updatedWorkPackage.id
                ) > -1
              ) {
                updatedWorkPackage = {
                  ...updatedWorkPackage,
                  parent_id: null
                };
              }
              return updatedWorkPackage;
            });
      return groupWorkPackages(state, workPackages, cadenceId);
    }
    case Types.UPDATE_WORK_PACKAGE_STATUS:
      workPackages = state.all.map((workPackage) => {
        if (workPackage.id === action.workPackageId) {
          return { ...workPackage, state: action.state };
        }
        return workPackage;
      });

      return groupWorkPackages(state, workPackages, cadenceId);
    case Types.SORT_WORK_PACKAGES: {
      const sorted = orderBy(state.all, [action.field], [action.order]);
      return {
        ...state,
        all: sorted
      };
    }
    case Types.SEND_WORK_PACKAGE:
      return { ...state, isSending: true };
    case Types.CANCEL_SEND_WORK_PACKAGE:
      return { ...state, isSending: false };
    case Types.WORK_PACKAGES_INITIAL_STATE:
      return {
        ...initialState,
        selectedSpace: state.selectedSpace,
        spaceSelected: state.spaceSelected
      };
    case Types.ADD_WRAP_PARENT: {
      const parent = state.all.find((w) => w.id === action.parentId);
      if (
        !parent ||
        state.parents.findIndex((p) => p.id === action.parentId) > -1
      ) {
        return { ...state };
      }
      const newParents = state.parents.map((p) => p);
      newParents.push(parent);
      return { ...state, parents: newParents };
    }
    case Types.REMOVE_WRAP_PARENT: {
      let shouldStay = true;
      const parents = state.parents.filter((parent) => {
        if (parent.id === action.parentId) {
          shouldStay = false;
        }
        return shouldStay;
      });
      return { ...state, parents };
    }
    case Types.RESET_WRAP_PARENT:
      return { ...state, parents: [] };
    case Types.ADD_WORK_PACKAGE_METRICS_BURNDOWN:
      metricsClone = cloneDeep(metrics);

      if (metricsClone[action.workPackageId]) {
        metricsClone[action.workPackageId].burndown = action.burndown;
      } else {
        metricsClone[action.workPackageId] = {
          burndown: action.burndown
        };
      }
      return {
        ...state,
        metrics: metricsClone
      };
    case Types.ADD_WORK_PACKAGE_METRICS_LEAN:
      metricsClone = cloneDeep(metrics);
      if (metricsClone[action.workPackageId]) {
        metricsClone[action.workPackageId].lean = action.lean;
      } else {
        metricsClone[action.workPackageId] = {
          cycles: action.lean
        };
      }
      return {
        ...state,
        metrics: metricsClone
      };
    case Types.ADD_WORK_PACKAGE_METRICS_IDLE:
      metricsClone = cloneDeep(metrics);
      if (metricsClone[action.workPackageId]) {
        metricsClone[action.workPackageId].idle = action.idle;
      } else {
        metricsClone[action.workPackageId] = {
          idle: action.idle
        };
      }
      return {
        ...state,
        metrics: metricsClone
      };
    case Types.ADD_WORK_PACKAGE_METRICS_CUMULATIVE:
      metricsClone = cloneDeep(metrics);
      if (metricsClone[action.workPackageId]) {
        metricsClone[action.workPackageId].cumulative = action.cumulative;
      } else {
        metricsClone[action.workPackageId] = {
          cumulative: action.cumulative
        };
      }
      return {
        ...state,
        metrics: metricsClone
      };
    case Types.ADD_WORK_PACKAGE_METRICS_GANTT:
      metricsClone = cloneDeep(metrics);
      if (metricsClone[action.workPackageId]) {
        metricsClone[action.workPackageId].gantt = action.gantt;
      } else {
        metricsClone[action.workPackageId] = {
          gantt: action.gantt
        };
      }
      return {
        ...state,
        metrics: metricsClone
      };

    case Types.RECEIVE_CHILDREN_PROGRESS:
      // metricsClone = cloneDeep(metrics);
      // metricsClone.childrenProgress = {...metrics.childrenProgress, ...action.childrenProgress  }
      return {
        ...state,
        metrics: {
          ...metrics,
          childrenProgress: {
            ...action.childrenProgress
          }
        }
      };

    case Types.UPDATE_WORK_PACKAGE_KIND: {
      const { organizationLevel } = action;
      workPackages = state.all.map((wp) => {
        const updatedWp = { ...wp };
        if (
          wp.result_type &&
          wp.result_type.organization_level_id === organizationLevel.id
        ) {
          updatedWp.result_type.kind = organizationLevel.name;
        }
        return updatedWp;
      });
      return groupWorkPackages(state, workPackages, cadenceId);
    }
    case Types.SET_SELECTED_SPACE:
      return {
        ...state,
        selectedSpace: action.selectedSpace,
        spaceSelected: isSpaceSelected(action.selectedSpace)
      };
    case Types.UPDATE_SELECTED_TEAM_SPACE:
      updatedSpace = {
        ...state.selectedSpace,
        ...action.team
      };

      return {
        ...state,
        selectedSpace: updatedSpace,
        spaceSelected: isSpaceSelected(action.selectedSpace)
      };

    case Types.UPDATE_SELECTED_PRODUCT_SPACE:
      // eslint-disable-next-line no-case-declarations
      const spreadTeams = [...state.selectedSpace.teams];
      // eslint-disable-next-line no-case-declarations
      const newTeams = spreadTeams.map((t) => {
        if (t.id === team.id) {
          delete team.products;
          return team;
        }
        return t;
      });

      updatedSpace = {
        ...state.selectedSpace,
        teams: newTeams
      };

      return {
        ...state,
        selectedSpace: updatedSpace,
        spaceSelected: isSpaceSelected(action.selectedSpace)
      };

    case Types.ADD_PRODUCT_TO_TEAM_SPACE:
      updatedSpace = { ...state.selectedSpace };
      updatedSpace.products = [...updatedSpace.products, product];

      return {
        ...state,
        selectedSpace: updatedSpace,
        spaceSelected: isSpaceSelected(action.selectedSpace)
      };

    case Types.ADD_TEAM_TO_PRODUCT_SPACE:
      updatedSpace = { ...state.selectedSpace };
      updatedSpace.teams = [...updatedSpace.teams, team];

      return {
        ...state,
        selectedSpace: updatedSpace,
        spaceSelected: isSpaceSelected(action.selectedSpace)
      };

    case Types.DELETE_PRODUCT_FROM_TEAM_SPACE:
      updatedSpace = { ...state.selectedSpace };
      updatedSpace.products = updatedSpace.products.filter(
        (p) => p.id !== productId
      );

      return {
        ...state,
        selectedSpace: updatedSpace,
        spaceSelected: isSpaceSelected(action.selectedSpace)
      };

    case Types.DELETE_TEAM_FROM_PRODUCT_SPACE:
      updatedSpace = { ...state.selectedSpace };
      updatedSpace.teams = updatedSpace.teams.filter((t) => t.id !== teamId);

      return {
        ...state,
        selectedSpace: updatedSpace,
        spaceSelected: isSpaceSelected(action.selectedSpace)
      };
    case Types.CLEAR_ACTIVE_SPINNER:
      return {
        ...state,
        isFetching: false,
        isSending: false,
        initialDataLoading: false
      };

    case Types.UPDATE_CADENCE: {
      const { cadence } = action;
      if (action.cadence.deleted_at) {
        // remove the soft-deleted cadence from all work packages
        return removeCadenceFromWorkPackages(cadence, state, cadenceId);
      }
      return { ...state };
    }

    case Types.DELETE_CADENCE: {
      const { cadence } = action;
      return removeCadenceFromWorkPackages(cadence, state, cadenceId);
    }

    default:
      return state;
  }
};

export default workPackageReducer;
